import {
  escapeHTML,
  renderEmptyState,
  renderMessageContent
} from "../../render/ui-helpers.js";
import {
  inferenceCapabilityFileAccept,
  inferenceCapabilityFileInputName,
  inferenceCapabilityMediaKind,
  inferenceCapabilityPromptPlaceholder,
  LLM_THREAD_CAPABILITY_OPTIONS,
  normalizeInferenceCapability
} from "../../inference-capabilities.js";
import {
  activeThreadTurnFromState,
  threadSendBlockReason
} from "../state.js";
import {
  llmCapabilityResolution,
  llmCapabilitySendBlockReason
} from "./capability-resolution.js";
import { filterLLMContextItems, MAX_LLM_CONTEXT_ITEM_REFS } from "./context-items.js";

export function renderLLMThreadComponent(state, options = {}) {
  const current = state.thread;
  const activeTurn = activeThreadTurnFromState(state);
  const hasActiveTurn = Boolean(activeTurn);
  const sendBlockReason = threadSendBlockReason(state, current, activeTurn);
  const agentLabel = String(options.agentLabel || current.agent_id || "unknown");
  const toolbarActionsHTML = String(options.toolbarActionsHTML || "");
  const isArchived = current.state === "archived" || current.state === "merged";
  const streamDotClass = streamStateDotClass(state.streamState);
  const capability = normalizeInferenceCapability(state.composerCapability);
  const promptPlaceholder = inferenceCapabilityPromptPlaceholder(capability);
  const mediaKind = inferenceCapabilityMediaKind(capability);
  const capabilityResolution = llmCapabilityResolution(current, capability);
  const capabilityBlockReason = llmCapabilitySendBlockReason(state, capability);
  const composerBlockReason = sendBlockReason || capabilityBlockReason;
  const canSubmit = composerBlockReason === "";
  const canDropMedia = Boolean(mediaKind && canUploadAttachments(options));
  const contextInfoHTML = [
    renderComposerContextStatus(state, current, capability, options),
    renderActiveContextChips(state, current, capability, options)
  ].filter(Boolean).join("");
  return `
    <div class="thread-component${options.embed ? " thread-component--embed" : ""}" data-thread-component>
      <div class="thread-toolbar">
        <div class="thread-toolbar__left">
          <span class="badge badge--${escapeHTML(current.state)}">${escapeHTML(current.state)}</span>
          <span class="badge badge--llm">LLM</span>
          ${streamDotClass ? `<span class="status-dot status-dot--${escapeHTML(streamDotClass)}" title="Stream: ${escapeHTML(state.streamState || "idle")}"></span>` : ""}
          <span class="thread-toolbar__meta">${escapeHTML(agentLabel)} / ${state.turns.length} turn${state.turns.length === 1 ? "" : "s"}</span>
        </div>
        <div class="thread-toolbar__right">
          ${hasActiveTurn ? `<button class="button button--sm button--danger" type="button" data-action="cancel-thread-run">Cancel turn</button>` : ""}
          ${toolbarActionsHTML}
        </div>
      </div>
      <div class="conversation-scroll">
        ${state.turns.length ? state.turns.map((item) => renderLLMThreadTurn(item, options)).join("") : renderEmptyState("No messages yet", "Send the first message to start the conversation.")}
      </div>
      ${isArchived ? `<div class="composer-v2 composer-v2--readonly"><div class="composer-v2__hint">${escapeHTML(options.readOnlyMessage || "This thread is read-only.")}</div></div>` : `
      <form id="thread-message-form" class="composer-v2${canDropMedia ? " composer-v2--droppable" : ""}" data-thread-id="${escapeHTML(current.id)}" data-thread-message-form enctype="multipart/form-data">
        ${canDropMedia ? `<div class="composer-v2__drop-overlay"><span>Drop files here</span></div>` : ""}
        ${composerBlockReason && !state.submitting ? `<div class="composer-v2__hint">${escapeHTML(composerBlockReason)}</div>` : ""}
        ${renderCapabilityResolutionBar(capabilityResolution, state, capability, options, contextInfoHTML)}
        ${renderComposerContextPanel(state, current, capability, options)}
        ${renderPendingAttachmentStrip(pendingAttachmentsForCapability(state, capability))}
        <div class="composer-v2__row">
          <div class="composer-v2__toolbar">
            <label class="composer-v2__mode-btn" title="Mode">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.65 1.65 0 00.33 1.82l.06.06a2 2 0 010 2.83 2 2 0 01-2.83 0l-.06-.06a1.65 1.65 0 00-1.82-.33 1.65 1.65 0 00-1 1.51V21a2 2 0 01-4 0v-.09A1.65 1.65 0 009 19.4a1.65 1.65 0 00-1.82.33l-.06.06a2 2 0 01-2.83 0 2 2 0 010-2.83l.06-.06A1.65 1.65 0 004.68 15a1.65 1.65 0 00-1.51-1H3a2 2 0 010-4h.09A1.65 1.65 0 004.6 9a1.65 1.65 0 00-.33-1.82l-.06-.06a2 2 0 112.83-2.83l.06.06A1.65 1.65 0 009 4.68a1.65 1.65 0 001-1.51V3a2 2 0 014 0v.09a1.65 1.65 0 001 1.51 1.65 1.65 0 001.82-.33l.06-.06a2 2 0 012.83 2.83l-.06.06a1.65 1.65 0 00-.33 1.82V9a1.65 1.65 0 001.51 1H21a2 2 0 010 4h-.09a1.65 1.65 0 00-1.51 1z"/></svg>
              <select name="capability_family" class="composer-v2__mode-select">
                ${LLM_THREAD_CAPABILITY_OPTIONS.map((item) => renderCapabilityOption(item.value, capability, item.label)).join("")}
              </select>
            </label>
            ${renderComposerContextButton(state, current, capability, options)}
            ${renderComposerMediaButtons(state, capability, options)}
          </div>
          <textarea name="prompt" placeholder="${escapeHTML(options.composePlaceholder || promptPlaceholder || "Message...")}" rows="2">${escapeHTML(state.composerDraft)}</textarea>
          ${hasActiveTurn ? `
            <button class="composer-v2__send composer-v2__send--danger" type="button" data-action="cancel-thread-run" title="Cancel active turn">&times;</button>
          ` : `
            <button class="composer-v2__send" type="submit" ${(canSubmit && !state.submitting) ? "" : "disabled"} title="Send">${state.submitting ? `<span class="composer-v2__send-dots">...</span>` : `<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><line x1="12" y1="19" x2="12" y2="5"/><polyline points="5 12 12 5 19 12"/></svg>`}</button>
          `}
        </div>
        ${renderCaptureStatus(state.captureState)}
      </form>
      `}
      ${renderContextItemPreviewModal(state, current)}
    </div>
  `;
}

function renderCapabilityResolutionBar(resolution, state, capability, options, contextChipsHTML = "") {
  const routeOverride = String(state?.composerRouteOverride || "").trim();
  const modelOverride = String(state?.composerModelOverride || "").trim();
  const hasOverrides = routeOverride || modelOverride;
  const isOpen = state?.composerOverrideOpen;
  const allowOverrides = options.embed !== true;

  const allRoutes = Array.isArray(options.inferenceRoutes) ? options.inferenceRoutes : [];
  const capabilityRoutes = allRoutes.filter((item) => item.enabled && item.capability_family === capability);
  const selectedRoute = capabilityRoutes.find((item) => item.id === state.composerRouteOverride) || null;
  const routeModels = selectedRoute
    ? uniqueStrings([selectedRoute.default_model, ...(Array.isArray(selectedRoute.advertised_models) ? selectedRoute.advertised_models : [])])
    : uniqueStrings(capabilityRoutes.flatMap((item) => [
      item.default_model,
      ...(Array.isArray(item.advertised_models) ? item.advertised_models : [])
    ]));
  const modelListID = "thread-composer-model-options";

  let hintText = "";
  if (hasOverrides) {
    const parts = [];
    if (routeOverride) {
      const routeName = capabilityRoutes.find((item) => item.id === routeOverride)?.name || routeOverride;
      parts.push(routeName);
    }
    if (modelOverride) parts.push(modelOverride);
    hintText = parts.join(" \u00b7 ");
  } else if (resolution && resolution.status) {
    const routeLabel = String(resolution.route_name || resolution.route_id || "").trim();
    const modelLabel = String(resolution.model || "").trim();
    switch (resolution.status) {
    case "configured":
      hintText = [routeLabel, modelLabel].filter(Boolean).join(" \u00b7 ");
      break;
    case "implicit":
      hintText = [routeLabel, modelLabel].filter(Boolean).join(" \u00b7 ");
      break;
    case "unavailable":
      hintText = String(resolution.reason || "Unavailable");
      break;
    }
  }

  if (!allowOverrides) {
    if (!hintText && !contextChipsHTML) {
      return "";
    }
    return `
      <div class="composer-v2__resolution-bar">
        <div class="composer-v2__resolution-line">
          ${hintText ? `<span class="composer-v2__resolution-static">${escapeHTML(hintText)}</span>` : ""}
          ${contextChipsHTML ? `<div class="composer-v2__resolution-chips">${contextChipsHTML}</div>` : ""}
        </div>
      </div>
    `;
  }

  if (!isOpen) {
    if (!hintText && !contextChipsHTML) {
      return `<div class="composer-v2__resolution-bar"><button class="composer-v2__resolution-toggle" type="button" data-action="toggle-composer-overrides" title="Route and model overrides">\u2699</button></div>`;
    }
    const resolutionControlHTML = hintText ? `
      <button class="composer-v2__resolution-closed" type="button" data-action="toggle-composer-overrides" title="Route and model overrides">
        <span>${escapeHTML(hintText)}</span>
        <span class="composer-v2__resolution-chevron">\u25BE</span>
      </button>
    ` : `<button class="composer-v2__resolution-toggle" type="button" data-action="toggle-composer-overrides" title="Route and model overrides">\u2699</button>`;
    return `
      <div class="composer-v2__resolution-bar">
        <div class="composer-v2__resolution-line">
          ${resolutionControlHTML}
          ${contextChipsHTML ? `<div class="composer-v2__resolution-chips">${contextChipsHTML}</div>` : ""}
        </div>
      </div>
    `;
  }

  return `
    <div class="composer-v2__resolution-bar">
      <div class="composer-v2__resolution-line">
        <button class="composer-v2__resolution-closed" type="button" data-action="toggle-composer-overrides" title="Collapse overrides">
          <span>${escapeHTML(hintText || "Overrides")}</span>
          <span class="composer-v2__resolution-chevron">\u25B4</span>
        </button>
        ${contextChipsHTML ? `<div class="composer-v2__resolution-chips">${contextChipsHTML}</div>` : ""}
      </div>
      <div class="composer-v2__override-inline">
        ${capabilityRoutes.length ? `
          <select name="route_id" class="composer-v2__override-select" title="Route override">
            <option value="">Default route</option>
            ${capabilityRoutes.map((item) => `<option value="${escapeHTML(item.id)}" ${state.composerRouteOverride === item.id ? "selected" : ""}>${escapeHTML(item.name)}</option>`).join("")}
          </select>
        ` : `
          <input name="route_id" class="composer-v2__override-input" placeholder="Route" value="${escapeHTML(state.composerRouteOverride || "")}">
        `}
        <input name="model" class="composer-v2__override-input" ${routeModels.length ? `list="${modelListID}"` : ""} placeholder="Model" value="${escapeHTML(state.composerModelOverride || "")}">
        ${routeModels.length ? `
          <datalist id="${modelListID}">
            ${routeModels.map((item) => `<option value="${escapeHTML(item)}"></option>`).join("")}
          </datalist>
        ` : ""}
      </div>
    </div>
  `;
}

function uniqueStrings(items) {
  const seen = new Set();
  const out = [];
  for (const item of items || []) {
    const value = String(item || "").trim();
    if (!value || seen.has(value)) {
      continue;
    }
    seen.add(value);
    out.push(value);
  }
  return out;
}

function renderLLMThreadTurn(item, options = {}) {
  const metaParts = [formatShortTime(item.created_at)];
  if (item.model) metaParts.push(item.model);
  if (item.usage_total_tokens) metaParts.push(`${item.usage_total_tokens} tok`);
  const userBody = renderInputParts(item, options);
  const assistantBody = renderOutputParts(item, options);

  return `
    <div class="message-group">
      <div class="message-bubble message-bubble--user">
        <div class="message-bubble__body">${userBody}</div>
      </div>
      <div class="message-turn-sep">
        Assistant &middot; ${escapeHTML(metaParts.filter(Boolean).join(" \u00b7 "))}
        ${item.request_log_id ? ` &middot; <a href="/models?tab=traffic&request=${encodeURIComponent(item.request_log_id)}" data-route>trace</a>` : ""}
      </div>
      <div class="message-bubble ${(item.state === "failed" || item.state === "canceled") ? "message-bubble--error" : "message-bubble--agent"}">
        <div class="message-bubble__body">${assistantBody}</div>
      </div>
    </div>
  `;
}

function renderInputParts(item, options = {}) {
  const parts = Array.isArray(item.input_parts) ? item.input_parts : [];
  const text = parts
    .filter((part) => part.type === "input_text" && part.text)
    .map((part) => renderMessageContent(part.text))
    .join("");
  const images = parts
    .filter((part) => part.type === "input_image_attachment" && part.attachment)
    .map((part) => renderAttachmentImage(
      part.attachment,
      "",
      renderAttachmentContextActions(part.attachment, options)
    ))
    .join("");
  const audio = parts
    .filter((part) => part.type === "input_audio_attachment" && part.attachment)
    .map((part) => renderAttachmentAudio(part.attachment))
    .join("");
  if (text || images || audio) {
    return `
      ${text ? `<div class="thread-richtext">${text}</div>` : ""}
      ${images ? `<div class="thread-image-grid">${images}</div>` : ""}
      ${audio ? `<div class="thread-audio-stack">${audio}</div>` : ""}
    `.trim();
  }
  return renderMessageContent(item.prompt || item.user_message || "");
}

function renderOutputParts(item, options = {}) {
  const isRunning = item.state === "running";
  const parts = Array.isArray(item.output_parts) ? item.output_parts : [];
  const text = parts
    .map((part, index) => renderOutputTextPart(item, part, index, isRunning, options))
    .filter(Boolean)
    .join("");
  const images = parts
    .filter((part) => part.type === "output_image_attachment" && part.attachment)
    .map((part) => renderAttachmentImage(
      part.attachment,
      part.revised_prompt,
      renderAttachmentContextActions(part.attachment, options)
    ))
    .join("");
  const audio = parts
    .filter((part) => part.type === "output_audio_attachment" && part.attachment)
    .map((part) => renderAttachmentAudio(part.attachment))
    .join("");

  if (item.state === "failed" || item.state === "canceled") {
    return renderMessageContent(item.error_message || (item.state === "canceled" ? "Turn canceled." : "Turn failed."));
  }
  if (!text && !images && !audio) {
    if (isRunning && item.assistant_message) {
      return renderMessageContent(item.assistant_message) + `<span class="stream-cursor">\u258D</span>`;
    }
    if (isRunning) {
      return `<span class="thinking-pulse">Thinking...</span>`;
    }
    return renderMessageContent(item.assistant_message || item.text_output_preview || "Waiting for response...");
  }
  return `
    ${text}
    ${images ? `<div class="thread-image-grid">${images}</div>` : ""}
    ${audio ? `<div class="thread-audio-stack">${audio}</div>` : ""}
  `.trim();
}

function renderOutputTextPart(item, part, index, isRunning, options = {}) {
  if (part.type !== "output_text" || !part.text) {
    return "";
  }
  const textHTML = renderMessageContent(part.text);
  if (item.capability_family === "audio_transcription" || item.capability_family === "audio_translation") {
    const meta = [
      part.language ? `Lang: ${escapeHTML(String(part.language))}` : "",
      part.response_format ? `Format: ${escapeHTML(String(part.response_format))}` : ""
    ].filter(Boolean).join(" · ");
    return `
      <div class="thread-output-card">
        ${meta ? `<div class="thread-output-card__meta">${meta}</div>` : ""}
        <div class="thread-richtext">${textHTML}</div>
        <div class="thread-output-card__actions">
          <button class="button button--sm button--ghost" type="button" data-action="use-output-text" data-turn-id="${escapeHTML(String(item.id || ""))}" data-output-index="${escapeHTML(String(index))}">Use in chat</button>
          ${renderOutputTextContextActions(item, index, options)}
        </div>
      </div>
    `;
  }
  return `<div class="thread-richtext">${textHTML}${isRunning ? `<span class="stream-cursor">\u258D</span>` : ""}</div>`;
}

function renderAttachmentImage(attachment, caption = "", actionsHTML = "") {
  const objectURL = String(attachment?.object_url || "").trim();
  const alt = escapeHTML(String(attachment?.filename || "attachment"));
  const captionHTML = String(caption || "").trim()
    ? `<figcaption class="thread-attachment__caption">${escapeHTML(String(caption || ""))}</figcaption>`
    : "";
  return `
    <figure class="thread-attachment">
      ${objectURL ? `<img class="thread-attachment__image" src="${escapeHTML(objectURL)}" alt="${alt}">` : `<div class="thread-attachment__placeholder">${alt}</div>`}
      ${captionHTML}
      ${actionsHTML ? `<div class="thread-output-card__actions">${actionsHTML}</div>` : ""}
    </figure>
  `;
}

function renderAttachmentAudio(attachment) {
  const objectURL = String(attachment?.object_url || "").trim();
  if (!objectURL) {
    return `<div class="thread-audio-card">${escapeHTML(String(attachment?.filename || "audio"))}</div>`;
  }
  return `
    <div class="thread-audio-card">
      <audio controls src="${escapeHTML(objectURL)}"></audio>
    </div>
  `;
}

function renderActiveContextChips(state, current, capability, options = {}) {
  if (capability !== "responses") {
    return "";
  }
  const managedContextItemIDs = canReadContextItems(options) ? contextItemIDSet(state?.contextItems) : null;
  const pinned = Array.isArray(current?.pinned_context_refs) ? current.pinned_context_refs.map((item) => ({ ...item, source: "pinned" })) : [];
  const oneShot = Array.isArray(state?.composerContextRefs) ? state.composerContextRefs.map((item) => ({ ...item, source: "one_shot" })) : [];
  const refs = [...pinned, ...oneShot].filter((item) => !isManagedContextItemRef(item, managedContextItemIDs));
  if (refs.length === 0) {
    return "";
  }
  return refs.map((item) => renderContextRefPill(item, state.turns || [], options)).join("");
}

function renderComposerContextStatus(state, current, capability, options = {}) {
  if (capability !== "responses" || !canReadContextItems(options)) {
    return "";
  }
  const availableCount = Array.isArray(state?.contextItems) ? state.contextItems.length : 0;
  const addedCount = activeContextItemIDSet(state, current).size;
  const activeClass = addedCount > 0 ? " composer-v2__context-status--active" : "";
  const title = `Context: ${addedCount} added / ${availableCount} available. ${MAX_LLM_CONTEXT_ITEM_REFS} context items max per turn.`;
  return `
    <button class="composer-v2__context-status${activeClass}" type="button" data-action="toggle-composer-context" title="${escapeHTML(title)}">
      <span>Context</span>
      <span>${escapeHTML(`${addedCount}/${availableCount}`)}</span>
    </button>
  `;
}

function activeContextItemIDSet(state, current) {
  const ids = new Set();
  const collect = (refs) => {
    for (const ref of refs || []) {
      const id = String(ref?.context_item_id || "").trim();
      if (id) {
        ids.add(id);
      }
    }
  };
  collect(current?.pinned_context_refs);
  collect(state?.composerContextRefs);
  return ids;
}

function renderComposerContextButton(state, current, capability, options = {}) {
  if (capability !== "responses" || !canReadContextItems(options)) {
    return "";
  }
  const pinnedCount = pinnedContextItemCount(current);
  const selectedCount = composerContextItemCount(state);
  const count = pinnedCount + selectedCount;
  return `
    <button class="composer-v2__icon-btn${state.composerContextPanelOpen ? " composer-v2__icon-btn--open" : ""}" type="button" data-action="toggle-composer-context" title="Context">
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M9 3h6l2 3h3a2 2 0 012 2v10a2 2 0 01-2 2H4a2 2 0 01-2-2V8a2 2 0 012-2h3l2-3z"/><path d="M8 12h8"/><path d="M8 16h5"/></svg>
      ${count > 0 ? `<span class="composer-v2__icon-count">${escapeHTML(String(count))}</span>` : ""}
    </button>
  `;
}

function renderComposerContextPanel(state, current, capability, options = {}) {
  if (capability !== "responses" || !state.composerContextPanelOpen || !canReadContextItems(options)) {
    return "";
  }
  const items = Array.isArray(state.contextItems) ? state.contextItems : [];
  const filterText = String(state.contextItemFilterText || "").trim();
  const filteredItems = filterLLMContextItems(items, filterText);
  const visibleItems = sortContextItemsForPanel(filteredItems, state, current);
  const contentType = state.contextItemDraftContentType === "text/markdown" ? "text/markdown" : "text/plain";
  const uploadName = String(state.contextItemUploadFilename || "").trim();
  const pinnedCount = pinnedContextItemCount(current);
  const selectedCount = composerContextItemCount(state);
  const activeContextItemIDs = activeContextItemIDSet(state, current);
  const remainingContextItemSlots = Math.max(0, MAX_LLM_CONTEXT_ITEM_REFS - activeContextItemIDs.size);
  const addableShownTotal = filteredItems.filter((item) => {
    const id = String(item?.id || "").trim();
    return id && !activeContextItemIDs.has(id);
  }).length;
  const addableShownCount = Math.min(remainingContextItemSlots, addableShownTotal);
  const queuedShownCount = filteredItems.filter((item) => hasComposerContextItem(state, String(item?.id || "").trim())).length;
  const canCreateContextItems = canWriteContextItems(options);
  const addOpen = canCreateContextItems && (items.length === 0
    || String(state.contextItemDraftTitle || "").trim()
    || String(state.contextItemDraftText || "").trim()
    || uploadName);
  const filterOpen = Boolean(state.contextItemFilterOpen || filterText);
  const contextFilterHTML = filterOpen ? `
    <div class="thread-context-picker__filter">
      <input name="context_filter_text" form="thread-context-item-controls" placeholder="Filter context" value="${escapeHTML(state.contextItemFilterText || "")}" autocomplete="off">
    </div>
  ` : "";
  const contextItemsListHTML = (!state.contextItemsLoading && !state.contextItemsError) ? `
    <div class="thread-context-picker__list">
      ${visibleItems.length ? visibleItems.map((item) => renderContextItemRow(item, state, current, options)).join("") : `<div class="thread-context-picker__empty">${items.length && filterText ? "No matching context" : "No saved context"}</div>`}
    </div>
  ` : "";
  return `
    <div class="thread-context-picker" data-context-panel>
      <div class="thread-context-picker__header">
        <div class="thread-context-picker__heading">
          <div class="thread-context-picker__title">Context</div>
          <div class="thread-context-picker__summary">
            <span>${escapeHTML(`${items.length} item${items.length === 1 ? "" : "s"}`)}</span>
            ${filterText ? `<span>${escapeHTML(`${filteredItems.length} shown`)}</span>` : ""}
            ${selectedCount > 0 ? `<span>${escapeHTML(`${selectedCount} queued`)}</span>` : ""}
            ${pinnedCount > 0 ? `<span>${escapeHTML(`${pinnedCount} pinned`)}</span>` : ""}
          </div>
        </div>
        <div class="thread-context-picker__header-actions">
          <button class="thread-context-picker__icon thread-context-picker__close" type="button" data-action="toggle-composer-context" title="Close context" aria-label="Close context">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><path d="M18 6L6 18"/><path d="M6 6l12 12"/></svg>
          </button>
          <button class="thread-context-picker__bulk-action" type="button" data-action="reference-all-context-items" title="Use all shown context items, up to the ${MAX_LLM_CONTEXT_ITEM_REFS} item turn limit" ${addableShownCount > 0 ? "" : "disabled"}>Use All</button>
          ${queuedShownCount > 0 ? `<button class="thread-context-picker__bulk-action" type="button" data-action="remove-all-context-items" title="Remove all shown queued context items from this turn">Clear Used</button>` : ""}
          <button class="thread-context-picker__icon${state.contextItemFilterOpen || filterText ? " thread-context-picker__icon--open" : ""}" type="button" data-action="toggle-context-filter" title="Filter context" aria-label="Filter context">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><path d="M3 5h18"/><path d="M7 12h10"/><path d="M10 19h4"/></svg>
          </button>
          <button class="thread-context-picker__icon" type="button" data-action="refresh-context-items" title="Refresh context" aria-label="Refresh context">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><path d="M21 12a9 9 0 11-2.64-6.36"/><path d="M21 3v6h-6"/></svg>
          </button>
        </div>
      </div>
      <div class="thread-context-picker__body">
        ${contextFilterHTML}
        ${state.contextItemsLoading ? `<div class="thread-context-picker__empty">Loading...</div>` : ""}
        ${state.contextItemsError ? `<div class="thread-context-picker__error">${escapeHTML(state.contextItemsError)}</div>` : ""}
        ${contextItemsListHTML}
      </div>
      ${canCreateContextItems ? `
      <details class="thread-context-picker__add" ${addOpen ? "open" : ""}>
        <summary>
          <span>Add context</span>
          <span class="thread-context-picker__limit">TXT/MD | 256 KiB</span>
        </summary>
        <div class="thread-context-picker__add-body">
          <div class="thread-context-picker__create">
            <input name="context_title" form="thread-context-item-controls" placeholder="Title" value="${escapeHTML(state.contextItemDraftTitle || "")}">
            <select name="context_content_type" form="thread-context-item-controls" title="Type">
              <option value="text/plain" ${contentType === "text/plain" ? "selected" : ""}>Plain</option>
              <option value="text/markdown" ${contentType === "text/markdown" ? "selected" : ""}>Markdown</option>
            </select>
            <textarea name="context_text" form="thread-context-item-controls" rows="3" placeholder="Text">${escapeHTML(state.contextItemDraftText || "")}</textarea>
            <button class="button button--sm" type="button" data-action="create-context-text">Add text</button>
          </div>
          <div class="thread-context-picker__upload">
            <label class="thread-context-picker__file">
              <input type="file" name="context_file" form="thread-context-item-controls" accept=".txt,.md,.markdown,text/plain,text/markdown" multiple>
              <span>${escapeHTML(uploadName || "Choose file")}</span>
            </label>
            <button class="button button--sm button--ghost" type="button" data-action="upload-context-file">Upload</button>
          </div>
        </div>
      </details>
      ` : ""}
    </div>
  `;
}

function sortContextItemsForPanel(items, state, current) {
  return [...items].map((item, index) => ({ item, index, rank: contextItemPanelRank(item, state, current) }))
    .sort((left, right) => left.rank - right.rank || left.index - right.index)
    .map((entry) => entry.item);
}

function contextItemPanelRank(item, state, current) {
  const id = String(item?.id || "").trim();
  if (id && hasComposerContextItem(state, id)) {
    return 0;
  }
  if (id && findPinnedContextItem(current, id)) {
    return 1;
  }
  return 2;
}

function renderContextItemRow(item, state, current, options = {}) {
  const id = String(item?.id || "").trim();
  const selected = hasComposerContextItem(state, id);
  const pin = findPinnedContextItem(current, id);
  const confirmingDelete = String(state.contextItemDeleteConfirmID || "").trim() === id;
  const previewText = contextItemPreview(item);
  const displayTitle = contextItemDisplayTitle(item, previewText);
  const title = escapeHTML(displayTitle);
  const secondary = escapeHTML(contextItemSecondaryLabel(item, displayTitle, previewText));
  const classes = [
    "thread-context-picker__item",
    selected ? "thread-context-picker__item--selected" : "",
    pin ? "thread-context-picker__item--pinned" : "",
    confirmingDelete ? "thread-context-picker__item--deleting" : ""
  ].filter(Boolean).join(" ");
  return `
    <div class="${classes}">
      <div class="thread-context-picker__item-main">
        <div class="thread-context-picker__item-title-row">
          <button class="thread-context-picker__item-title" type="button" data-action="preview-context-item" data-context-item-id="${escapeHTML(id)}" title="Preview context">${title}</button>
          ${renderContextItemBadges(item, selected, pin)}
        </div>
        ${secondary ? `<div class="thread-context-picker__item-secondary" title="${secondary}">${secondary}</div>` : ""}
      </div>
      ${confirmingDelete && canWriteContextItems(options) ? renderContextItemDeleteConfirm(id) : renderContextItemActions(id, selected, pin, options)}
    </div>
  `;
}

function renderContextItemPreviewModal(state, current) {
  const previewID = String(state?.contextItemPreviewID || "").trim();
  if (!previewID) {
    return "";
  }
  const item = findContextItemByID(state, previewID)
    || findPinnedContextItem(current, previewID)?.context_item
    || { id: previewID, title: "Context" };
  const previewText = contextItemPreview(item);
  const displayTitle = contextItemDisplayTitle(item, previewText);
  const title = escapeHTML(displayTitle);
  const loading = Boolean(state?.contextItemPreviewLoading);
  const error = String(state?.contextItemPreviewError || "").trim();
  const bodyText = contextItemFullPreviewText(item);
  const source = contextItemSourceLabel(item);
  return `
    <div class="thread-context-preview" role="dialog" aria-modal="true" aria-label="Context preview">
      <button class="thread-context-preview__backdrop" type="button" data-action="close-context-preview" aria-label="Close context preview"></button>
      <section class="thread-context-preview__modal">
        <header class="thread-context-preview__header">
          <div class="thread-context-preview__heading">
            <div class="thread-context-preview__title" title="${title}">${title}</div>
            <div class="thread-context-preview__meta">
              ${renderContextItemBadges(item, false, findPinnedContextItem(current, previewID))}
              ${source ? `<span title="${escapeHTML(source)}">${escapeHTML(source)}</span>` : ""}
            </div>
          </div>
          <button class="thread-context-preview__close" type="button" data-action="close-context-preview" aria-label="Close context preview">&times;</button>
        </header>
        <div class="thread-context-preview__body">
          ${loading ? `<div class="thread-context-preview__empty">Loading full context...</div>` : ""}
          ${error ? `<div class="thread-context-preview__error">${escapeHTML(error)}</div>` : ""}
          ${(!loading && !error) ? `<div class="thread-richtext thread-context-preview__content">${renderMessageContent(bodyText || "No preview available.")}</div>` : ""}
        </div>
      </section>
    </div>
  `;
}

function renderContextItemActions(id, selected, pin, options = {}) {
  const canChangePins = canWriteContextPins(options);
  const canDeleteItems = canWriteContextItems(options);
  return `
    <div class="thread-context-picker__item-actions">
      ${renderContextItemUseAction(id, selected, pin)}
      <button class="thread-context-picker__action thread-context-picker__action--secondary" type="button" data-action="insert-context-item" data-context-item-id="${escapeHTML(id)}" title="Insert into message" aria-label="Insert into message">${renderContextActionIcon("insert")}<span>Insert</span></button>
      ${canChangePins && pin
        ? `<button class="thread-context-picker__action thread-context-picker__action--secondary thread-context-picker__action--active" type="button" data-action="unpin-context-ref" data-pin-id="${escapeHTML(String(pin.id || ""))}" title="Unpin" aria-label="Unpin">${renderContextActionIcon("pin")}<span>Pinned</span></button>`
        : canChangePins
          ? `<button class="thread-context-picker__action thread-context-picker__action--secondary" type="button" data-action="pin-context-ref" data-ref-type="context_item" data-context-item-id="${escapeHTML(id)}" title="Pin to thread" aria-label="Pin to thread">${renderContextActionIcon("pin")}<span>Pin</span></button>`
          : pin ? `<span class="thread-context-picker__action thread-context-picker__action--secondary thread-context-picker__action--active" title="Pinned to thread">${renderContextActionIcon("pin")}<span>Pinned</span></span>` : ""}
      ${canDeleteItems ? `<button class="thread-context-picker__action thread-context-picker__action--danger thread-context-picker__action--icon" type="button" data-action="delete-context-item" data-context-item-id="${escapeHTML(id)}" title="Delete context item" aria-label="Delete context item">${renderContextActionIcon("trash")}</button>` : ""}
    </div>
  `;
}

function renderContextItemUseAction(id, selected, pin) {
  if (selected) {
    return `<button class="thread-context-picker__action thread-context-picker__action--primary thread-context-picker__action--active" type="button" data-action="remove-context-ref" data-ref-type="context_item" data-context-item-id="${escapeHTML(id)}" title="Remove from this turn">${renderContextActionIcon("check")}<span>Using</span></button>`;
  }
  if (pin) {
    return `<button class="thread-context-picker__action thread-context-picker__action--primary thread-context-picker__action--active" type="button" title="Pinned to thread" disabled>${renderContextActionIcon("check")}<span>Using</span></button>`;
  }
  return `<button class="thread-context-picker__action thread-context-picker__action--primary" type="button" data-action="reference-context-item" data-context-item-id="${escapeHTML(id)}" title="Use this turn">${renderContextActionIcon("arrow")}<span>Use</span></button>`;
}

function renderContextItemDeleteConfirm(id) {
  return `
    <div class="thread-context-picker__delete-confirm">
      <span>Delete?</span>
      <button class="thread-context-picker__action" type="button" data-action="cancel-delete-context-item">Cancel</button>
      <button class="thread-context-picker__action thread-context-picker__action--danger" type="button" data-action="confirm-delete-context-item" data-context-item-id="${escapeHTML(id)}">Delete</button>
    </div>
  `;
}

function renderContextItemBadges(item, selected, pin) {
  const badges = [
    selected ? "This turn" : "",
    pin ? "Pinned" : "",
    contextItemSourceTypeLabel(item),
    contextItemTypeLabel(item),
    contextItemSizeLabel(item)
  ].filter(Boolean);
  if (badges.length === 0) {
    return "";
  }
  return `<div class="thread-context-picker__item-badges">${badges.map((badge) => `<span>${escapeHTML(badge)}</span>`).join("")}</div>`;
}

function renderContextActionIcon(name) {
  switch (name) {
  case "check":
    return `<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M20 6L9 17l-5-5"/></svg>`;
  case "arrow":
    return `<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M5 12h14"/><path d="M13 6l6 6-6 6"/></svg>`;
  case "insert":
    return `<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M4 6h16"/><path d="M4 18h16"/><path d="M8 12h8"/><path d="M12 9v6"/></svg>`;
  case "pin":
    return `<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M12 17v5"/><path d="M5 17h14"/><path d="M8 3h8l-1 8 3 3v3H6v-3l3-3-1-8z"/></svg>`;
  case "trash":
    return `<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M3 6h18"/><path d="M8 6V4h8v2"/><path d="M6 6l1 14h10l1-14"/><path d="M10 11v5"/><path d="M14 11v5"/></svg>`;
  default:
    return "";
  }
}

function renderComposerMediaButtons(state, capability, options = {}) {
  const mediaKind = inferenceCapabilityMediaKind(capability);
  if (mediaKind && !canUploadAttachments(options)) {
    return "";
  }
  const fileInputName = inferenceCapabilityFileInputName(capability);
  const fileAccept = inferenceCapabilityFileAccept(capability);
  const recording = state.captureState?.status === "recording" && state.captureState?.kind === "audio";
  if (mediaKind === "image") {
    return `
      <input type="file" name="${escapeHTML(fileInputName)}" accept="${escapeHTML(fileAccept)}" multiple style="display:none" data-file-input>
      <button class="composer-v2__icon-btn" type="button" data-action="trigger-file-input" title="Attach images">
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M21.44 11.05l-9.19 9.19a6 6 0 01-8.49-8.49l9.19-9.19a4 4 0 015.66 5.66l-9.2 9.19a2 2 0 01-2.83-2.83l8.49-8.48"/></svg>
      </button>
      <button class="composer-v2__icon-btn" type="button" data-action="capture-camera-image" title="Camera">
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M23 19a2 2 0 01-2 2H3a2 2 0 01-2-2V8a2 2 0 012-2h4l2-3h6l2 3h4a2 2 0 012 2z"/><circle cx="12" cy="13" r="4"/></svg>
      </button>
      <button class="composer-v2__icon-btn" type="button" data-action="capture-screenshot" title="Screenshot">
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="2" y="3" width="20" height="14" rx="2"/><line x1="8" y1="21" x2="16" y2="21"/><line x1="12" y1="17" x2="12" y2="21"/></svg>
      </button>
    `;
  }
  if (mediaKind === "audio") {
    return `
      <input type="file" name="${escapeHTML(fileInputName)}" accept="${escapeHTML(fileAccept)}" style="display:none" data-file-input>
      <button class="composer-v2__icon-btn" type="button" data-action="trigger-file-input" title="Attach audio">
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M21.44 11.05l-9.19 9.19a6 6 0 01-8.49-8.49l9.19-9.19a4 4 0 015.66 5.66l-9.2 9.19a2 2 0 01-2.83-2.83l8.49-8.48"/></svg>
      </button>
      <button class="composer-v2__icon-btn${recording ? " composer-v2__icon-btn--active" : ""}" type="button" data-action="${recording ? "stop-audio-capture" : "start-audio-capture"}" title="${recording ? "Stop recording" : "Record microphone"}">
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M12 1a3 3 0 00-3 3v8a3 3 0 006 0V4a3 3 0 00-3-3z"/><path d="M19 10v2a7 7 0 01-14 0v-2"/><line x1="12" y1="19" x2="12" y2="23"/></svg>
      </button>
    `;
  }
  return "";
}

function renderContextRefPill(item, turns, options = {}) {
  const body = renderContextRefLabel(item, turns);
  const icon = item?.source === "pinned" ? "\uD83D\uDCCC" : "\u2192";
  if (item?.source === "pinned") {
    return `
      <span class="thread-context-pill">
        <span class="thread-context-pill__icon">${icon}</span>
        <span class="thread-context-pill__text">${body}</span>
        ${canWriteContextPins(options) ? `<button class="thread-context-pill__close" type="button" data-action="unpin-context-ref" data-pin-id="${escapeHTML(String(item.id || ""))}" title="Unpin">&times;</button>` : ""}
      </span>
    `;
  }
  return `
    <span class="thread-context-pill">
      <span class="thread-context-pill__icon">${icon}</span>
      <span class="thread-context-pill__text">${body}</span>
      <button class="thread-context-pill__close" type="button" data-action="remove-context-ref" ${renderContextRefDataset(item)} title="Remove">&times;</button>
    </span>
  `;
}

function renderPendingAttachmentStrip(items) {
  if (!Array.isArray(items) || items.length === 0) {
    return "";
  }
  return `
    <div class="composer-v2__attachments-strip">
      ${items.map((item) => renderPendingAttachment(item)).join("")}
    </div>
  `;
}

function renderPendingAttachment(item) {
  const name = escapeHTML(String(item?.filename || item?.media_kind || "attachment"));
  const objectURL = escapeHTML(String(item?.object_url || ""));
  const mediaKind = String(item?.media_kind || "").trim();
  const preview = mediaKind === "image" && objectURL
    ? `<img class="composer-v2__pending-preview" src="${objectURL}" alt="${name}">`
    : `<div class="composer-v2__pending-icon">${escapeHTML(mediaKind === "audio" ? "\u266B" : "\u25A1")}</div>`;
  return `
    <div class="composer-v2__pending-item">
      ${preview}
      <div class="composer-v2__pending-name">${name}</div>
      <button class="composer-v2__pending-remove" type="button" data-action="remove-pending-attachment" data-pending-id="${escapeHTML(String(item?.id || ""))}" title="Remove">&times;</button>
    </div>
  `;
}

function renderCaptureStatus(captureState) {
  const kind = String(captureState?.kind || "").trim();
  const status = String(captureState?.status || "").trim();
  if (!kind || !status || status === "idle") {
    return "";
  }
  let message = "Preparing capture...";
  if (kind === "image" && status === "capturing") {
    message = "Waiting for browser capture permission...";
  } else if (kind === "audio" && status === "capturing") {
    message = "Waiting for microphone permission...";
  } else if (kind === "audio" && status === "recording") {
    message = "Recording microphone input. Stop when you are done.";
  } else if (kind === "audio" && status === "processing") {
    message = "Processing microphone capture...";
  }
  return `<div class="composer-v2__hint">${escapeHTML(message)}</div>`;
}

function pendingAttachmentsForCapability(state, capability) {
  const mediaKind = inferenceCapabilityMediaKind(capability);
  if (!mediaKind) {
    return [];
  }
  return (state?.pendingAttachments || []).filter((item) => item?.media_kind === mediaKind);
}

function renderContextRefLabel(item, turns) {
  if (item?.type === "attachment" && item?.attachment) {
    return escapeHTML(String(item.attachment.filename || item.attachment.id || "attachment"));
  }
  if (item?.type === "turn_output_text") {
    const text = resolveOutputTextPreview(turns, item.turn_id, item.output_index);
    if (text) {
      return escapeHTML(text);
    }
    return escapeHTML(String(item.turn_id || "turn output"));
  }
  if (item?.type === "context_item") {
    const contextItem = item.context_item || {};
    return escapeHTML(String(contextItem.title || contextItem.filename || contextItem.text_preview || item.context_item_id || "context item"));
  }
  return escapeHTML(String(item?.type || "context"));
}

function hasComposerContextItem(state, contextItemID) {
  return (state?.composerContextRefs || []).some((item) =>
    item?.type === "context_item" && String(item.context_item_id || "").trim() === contextItemID
  );
}

function composerContextItemCount(state) {
  return (state?.composerContextRefs || []).filter((item) =>
    item?.type === "context_item" && String(item.context_item_id || "").trim()
  ).length;
}

function findPinnedContextItem(current, contextItemID) {
  return (current?.pinned_context_refs || []).find((item) =>
    item?.type === "context_item" && String(item.context_item_id || "").trim() === contextItemID
  ) || null;
}

function pinnedContextItemCount(current) {
  return (current?.pinned_context_refs || []).filter((item) =>
    item?.type === "context_item" && String(item.context_item_id || "").trim()
  ).length;
}

function findContextItemByID(state, contextItemID) {
  const normalizedID = String(contextItemID || "").trim();
  if (!normalizedID) {
    return null;
  }
  return (state?.contextItems || []).find((item) => String(item?.id || "").trim() === normalizedID) || null;
}

function contextItemIDSet(items) {
  const ids = new Set();
  for (const item of items || []) {
    const id = String(item?.id || "").trim();
    if (id) {
      ids.add(id);
    }
  }
  return ids;
}

function isManagedContextItemRef(item, contextItemIDs) {
  if (!contextItemIDs || contextItemIDs.size === 0 || item?.type !== "context_item") {
    return false;
  }
  return contextItemIDs.has(String(item.context_item_id || "").trim());
}

function canReadContextItems(options = {}) {
  const permission = contextPermission(options.contextPermissions, "contextItems");
  return permission === "read" || permission === "write";
}

function canWriteContextItems(options = {}) {
  return contextPermission(options.contextPermissions, "contextItems") === "write";
}

function canWriteContextPins(options = {}) {
  return contextPermission(options.contextPermissions, "contextPins") === "write";
}

function canUploadAttachments(options = {}) {
  const permissions = options.contextPermissions;
  if (!permissions) {
    return true;
  }
  return String(permissions.attachments || "").trim().toLowerCase() === "upload";
}

function contextPermission(permissions, key) {
  if (!permissions) {
    return "write";
  }
  return String(permissions[key] || "").trim().toLowerCase() || "none";
}

function contextItemPreview(item) {
  const text = stripContextItemPreviewEnvelope(String(item?.text_preview || item?.text || "")).trim();
  if (!text) {
    return "";
  }
  return text.length > 220 ? `${text.slice(0, 217)}...` : text;
}

function contextItemFullPreviewText(item) {
  const text = String(item?.text || item?.text_preview || "").trim();
  return stripContextItemPreviewEnvelope(text).trim();
}

function stripContextItemPreviewEnvelope(value) {
  const text = String(value || "").trim();
  if (!text) {
    return "";
  }
  const contentMatch = text.match(/(?:^|\n)#{1,3}\s*Content\s*\n/i);
  if (contentMatch && contentMatch.index !== undefined) {
    return text.slice(contentMatch.index + contentMatch[0].length).trim();
  }
  const lines = text.split("\n");
  let index = 0;
  while (index < lines.length && !lines[index].trim()) {
    index += 1;
  }
  if (index < lines.length && /^#{1,3}\s+/.test(lines[index].trim())) {
    index += 1;
    while (index < lines.length && isContextItemMetadataLine(lines[index])) {
      index += 1;
    }
    while (index < lines.length && !lines[index].trim()) {
      index += 1;
    }
    if (index < lines.length) {
      return lines.slice(index).join("\n").trim();
    }
  }
  return text;
}

function isContextItemMetadataLine(line) {
  const value = String(line || "").trim();
  if (!value) {
    return true;
  }
  return /^-\s*(Include Mode|Updated|Owner ID|Shared|Relations|Source|Provider|Scope|Entity|Thread ID)\s*:/i.test(value);
}

function contextItemPreviewBody(previewText, displayTitle) {
  const lines = String(previewText || "").split("\n").map((line) => line.trim()).filter(Boolean);
  if (lines.length === 0) {
    return "";
  }
  const title = cleanContextItemTitle(displayTitle);
  if (title && cleanContextItemTitle(lines[0]) === title) {
    return lines.slice(1).join(" ");
  }
  return lines.join(" ");
}

function contextItemSecondaryLabel(item, displayTitle, previewText) {
  const preview = sanitizeContextItemPreview(contextItemPreviewBody(previewText, displayTitle));
  if (preview) {
    return preview;
  }
  const source = contextItemSourceLabel(item);
  if (source && cleanContextItemTitle(source) !== cleanContextItemTitle(displayTitle)) {
    return source;
  }
  return "";
}

function sanitizeContextItemPreview(value) {
  let text = String(value || "")
    .replace(/<!--[\s\S]*?-->/g, " ")
    .replace(/!\[[^\]]*]\([^)]*\)/g, " ")
    .replace(/\[[^\]]+]\(data:[^)]+\)/g, " ")
    .replace(/\s+/g, " ")
    .trim();
  if (!text) {
    return "";
  }
  if (/data:image\/[a-z0-9.+-]+;base64/i.test(text) || /base64,[a-z0-9+/=]{40,}/i.test(text)) {
    return "";
  }
  if (/[a-z0-9+/=]{80,}/i.test(text)) {
    return "";
  }
  text = text.replace(/^#+\s*/, "").trim();
  return text.length > 140 ? `${text.slice(0, 137)}...` : text;
}

function contextItemDisplayTitle(item, previewText = "") {
  const title = cleanContextItemTitle(String(item?.title || "").trim());
  if (title && (!isOpaqueContextItemTitle(title) || !previewText)) {
    return stripContextItemTitlePrefix(title, item);
  }
  const previewTitle = cleanContextItemTitle(String(previewText || "").split("\n").map((line) => line.trim()).filter(Boolean)[0] || "");
  if (previewTitle) {
    const cleanedPreviewTitle = stripContextItemTitlePrefix(previewTitle, item);
    return cleanedPreviewTitle.length > 96 ? `${cleanedPreviewTitle.slice(0, 93)}...` : cleanedPreviewTitle;
  }
  return title || String(item?.filename || item?.source_path || item?.id || "Context").trim();
}

function stripContextItemTitlePrefix(value, item) {
  const title = String(value || "").trim();
  const label = contextItemSourceTypeLabel(item);
  if (!title || !label) {
    return title;
  }
  const pattern = new RegExp(`^${escapeRegExp(label)}\\s*:\\s*`, "i");
  return title.replace(pattern, "").trim() || title;
}

function cleanContextItemTitle(value) {
  return String(value || "")
    .replace(/^#+\s*/, "")
    .replace(/\s+/g, " ")
    .trim();
}

function isOpaqueContextItemTitle(value) {
  const normalized = String(value || "").trim();
  return /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(normalized)
    || /^[0-9a-f]{20,}$/i.test(normalized)
    || /^lctx[_-]/i.test(normalized);
}

function contextItemSourceLabel(item) {
  return [item?.source_path, item?.filename, item?.source_id]
    .map((value) => String(value || "").trim())
    .filter(Boolean)[0] || "";
}

function contextItemTypeLabel(item) {
  const contentType = String(item?.content_type || "").trim().toLowerCase();
  if (contentType === "text/markdown") {
    return "MD";
  }
  if (contentType === "text/plain") {
    return "TXT";
  }
  return "";
}

function contextItemSourceTypeLabel(item) {
  const metadata = contextItemSourceMetadata(item);
  const entityKind = String(metadata?.entity_kind || "").trim();
  if (entityKind) {
    return titleCaseContextLabel(entityKind);
  }
  const sourceKind = String(item?.source_kind || "").trim();
  if (sourceKind === "file") {
    return "File";
  }
  return "";
}

function contextItemSourceMetadata(item) {
  const metadata = item?.source_metadata;
  if (!metadata) {
    return {};
  }
  if (typeof metadata === "object" && !Array.isArray(metadata)) {
    return metadata;
  }
  if (typeof metadata === "string") {
    try {
      const parsed = JSON.parse(metadata);
      return parsed && typeof parsed === "object" && !Array.isArray(parsed) ? parsed : {};
    } catch {
      return {};
    }
  }
  return {};
}

function titleCaseContextLabel(value) {
  return String(value || "")
    .split(/[_\s-]+/)
    .filter(Boolean)
    .map((part) => `${part.slice(0, 1).toUpperCase()}${part.slice(1).toLowerCase()}`)
    .join(" ");
}

function escapeRegExp(value) {
  return String(value || "").replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

function contextItemSizeLabel(item) {
  const bytes = Number(item?.size_bytes || 0);
  if (!Number.isFinite(bytes) || bytes <= 0) {
    return "";
  }
  if (bytes < 1024) {
    return `${bytes} B`;
  }
  if (bytes < 1024 * 1024) {
    return `${Math.round(bytes / 1024)} KB`;
  }
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function renderCapabilityOption(value, currentValue, label) {
  return `<option value="${escapeHTML(value)}" ${value === currentValue ? "selected" : ""}>${escapeHTML(label)}</option>`;
}

function renderAttachmentContextActions(attachment, options = {}) {
  const attachmentID = String(attachment?.id || "").trim();
  if (!attachmentID || attachmentID.startsWith("local-")) {
    return "";
  }
  return `
    <button class="button button--sm button--ghost" type="button" data-action="reference-attachment" data-attachment-id="${escapeHTML(attachmentID)}">Reference next</button>
    ${canWriteContextPins(options) ? `<button class="button button--sm button--ghost" type="button" data-action="pin-context-ref" data-ref-type="attachment" data-attachment-id="${escapeHTML(attachmentID)}">Pin</button>` : ""}
  `;
}

function renderOutputTextContextActions(item, index, options = {}) {
  return `
    <button class="button button--sm button--ghost" type="button" data-action="reference-output-text" data-turn-id="${escapeHTML(String(item.id || ""))}" data-output-index="${escapeHTML(String(index))}">Reference next</button>
    ${canWriteContextPins(options) ? `<button class="button button--sm button--ghost" type="button" data-action="pin-context-ref" data-ref-type="turn_output_text" data-turn-id="${escapeHTML(String(item.id || ""))}" data-output-index="${escapeHTML(String(index))}">Pin</button>` : ""}
  `;
}

function renderContextRefDataset(item) {
  if (item?.type === "attachment") {
    return `data-ref-type="attachment" data-attachment-id="${escapeHTML(String(item.attachment_id || ""))}"`;
  }
  if (item?.type === "context_item") {
    return `data-ref-type="context_item" data-context-item-id="${escapeHTML(String(item.context_item_id || ""))}"`;
  }
  return `data-ref-type="turn_output_text" data-turn-id="${escapeHTML(String(item.turn_id || ""))}" data-output-index="${escapeHTML(String(item.output_index || 0))}"`;
}

function resolveOutputTextPreview(turns, turnID, outputIndex) {
  const turn = (turns || []).find((item) => String(item?.id || "") === String(turnID || ""));
  const part = Array.isArray(turn?.output_parts) ? turn.output_parts[Number(outputIndex)] : null;
  const text = typeof part?.text === "string" ? part.text.trim() : "";
  if (!text) {
    return "";
  }
  return text.length > 48 ? `${text.slice(0, 45)}...` : text;
}

function streamStateDotClass(streamState) {
  switch (streamState) {
  case "connecting":
  case "connected":
    return "ok";
  case "error":
    return "error";
  default:
    return "";
  }
}

function formatShortTime(value) {
  if (!value) {
    return "";
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return "";
  }
  return date.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" });
}
